import React, { useMemo } from 'react';
import { useLocalSearchParams } from 'expo-router';
import { defineMessages, useIntl } from 'react-intl';
import { View } from 'react-native';

import { useResponsive, VStack, type AddressType } from '@sg/garnish';
import { FlattenedOrderStatuses, OrderType } from '@sg/graphql-schema';

import { OrderStatus } from '@order/features-order-status';
import { Receipt, useReceiptQuery, type ReceiptQuery } from '@order/features-receipt';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useContactUsHandler } from '@order/shared-hooks';
import { useTrackEffect } from '@order/Telemetry';
import { parsePayments } from '@order/utils';

import { AppFooter, LoadingAnimation, NotFoundView, SignedOutView } from '../../components';
import { useIsLoggedIn, useIsLoggingIn } from '../../context/AuthMachine';

export const ReceiptScreen = () => {
  const { receiptId } = useLocalSearchParams<{ receiptId: string }>();

  const isLoggedIn = useIsLoggedIn();
  const isLoggingIn = useIsLoggingIn();

  const [{ data, fetching, error }] = useReceiptQuery({
    requestPolicy: 'network-only',
    variables: { id: receiptId },
    pause: isLoggingIn,
  });

  // ─── Flag Check ───────────────────────────────────────────────────────────────────

  const isReceiptScreenEnabled = useFeatureFlag('cels-4664-enable-receipt-screen');

  if (!isReceiptScreenEnabled) {
    return <NotFoundView />;
  }

  // ─────────────────────────────────────────────────────────────────────────────

  const receiptResponse = data?.receipt;

  if (isLoggingIn) return <LoadingAnimation />;

  if (fetching && !receiptResponse) return <LoadingAnimation />;

  // NOTE: Claimed by different user state
  if (receiptResponse?.__typename === 'ReceiptAlreadyClaimed' && isLoggedIn)
    return <ClaimedReceiptView />;
  if (receiptResponse?.__typename === 'ReceiptAlreadyClaimed') return <SignedOutView />;

  if (receiptResponse?.__typename === 'ReceiptNotFound') return <NotFoundReceiptView />;

  if (receiptResponse?.__typename === 'ExpiredReceipt') return <ExpiredReceiptView />;
  if (error) return <UndefinedErrorReceiptView />;

  if (receiptResponse?.__typename === 'Receipt')
    return <ReceiptScreenReady isLoggedIn={isLoggedIn} receiptData={receiptResponse} />;

  return <UndefinedErrorReceiptView />;
};

const ReceiptScreenReady = (props: ReceiptScreenReadyProps) => {
  const { receiptData, isLoggedIn } = props;

  const { currentBreakpoint } = useResponsive();

  const { handleContactUs } = useContactUsHandler('receipt');

  // ─── Parse data ───────────────────────────────────────────────────────────────────

  const isClaimed = receiptData.isClaimed;

  const receiptId = receiptData.id;
  const order = receiptData.order;
  const orderId = receiptData.order.id;
  const orderStatus = FlattenedOrderStatuses.Completed;
  const orderTotal = order.total ?? undefined;

  const isRestaurantOutpost = false;
  const orderType = getOrderType(order.orderType, isRestaurantOutpost);

  const {
    id: restaurantId,
    name: locationName,
    address: locationAddress,
    city: locationCity,
    state: locationState,
    zipCode: locationZipCode,
    latitude: locationLatitude,
    longitude: locationLongitude,
  } = order.restaurant;

  const payments = useMemo(() => parsePayments(order.payments), [order.payments]);

  // ─────────────────────────────────────────────────────────────────────────────

  useTrackEffect('receipt.screen_viewed', { isLoggedIn, isClaimed });

  // ─────────────────────────────────────────────────────────────────────────────

  if (currentBreakpoint.isXS) {
    return (
      <OrderStatus.Layout.ContainerWrapper>
        <OrderStatus.Layout.Container>
          <OrderStatus.Layout.Info>
            <OrderStatus.Info
              orderId={orderId}
              orderType={orderType}
              orderStatus={orderStatus}
              orderTotal={orderTotal}
              wantedTime={order.wantedTime}
              vendorId={order.vendorId}
              locationName={locationName}
              locationAddress={locationAddress}
              isPendingFeedback={order.isPendingFeedback}
              isTrackable={false}
              canReorder={false}
              isPast
            />
          </OrderStatus.Layout.Info>

          <OrderStatus.Layout.Map>
            <OrderStatus.Map
              orderType={orderType}
              orderStatus={orderStatus}
              locationName={locationName}
              locationLatitude={locationLatitude}
              locationLongitude={locationLongitude}
            />
          </OrderStatus.Layout.Map>

          <OrderStatus.Layout.Content>
            <VStack gap={0} divider={<OrderStatus.Divider />}>
              <Receipt.ClaimBanner
                isLoggedIn={isLoggedIn}
                isClaimed={isClaimed}
                receiptId={receiptId}
              />

              <OrderStatus.Address
                orderType={orderType}
                locationName={locationName}
                locationAddress={locationAddress}
                locationCity={locationCity}
                locationState={locationState}
                locationZipCode={locationZipCode}
              />

              <View>
                <Receipt.LineItems lineItems={order.lineItems} />

                <OrderStatus.Ledger ledger={order.ledger} />
              </View>

              <OrderStatus.Payments payments={payments} />
            </VStack>

            <OrderStatus.ContactUs
              orderId={orderId}
              canCancel={false}
              restaurantId={restaurantId}
              onContactUs={handleContactUs}
              shouldHideCancelOption
            />
          </OrderStatus.Layout.Content>
        </OrderStatus.Layout.Container>
      </OrderStatus.Layout.ContainerWrapper>
    );
  }

  return (
    <OrderStatus.Layout.ContainerWrapper>
      <OrderStatus.Layout.Container>
        <OrderStatus.Layout.Map>
          <OrderStatus.Map
            orderType={orderType}
            orderStatus={orderStatus}
            locationName={locationName}
            locationLatitude={locationLatitude}
            locationLongitude={locationLongitude}
          />
        </OrderStatus.Layout.Map>

        <OrderStatus.Layout.SidePanel>
          <OrderStatus.Layout.Info>
            <OrderStatus.Info
              orderId={orderId}
              orderType={orderType}
              orderStatus={orderStatus}
              orderTotal={orderTotal}
              wantedTime={order.wantedTime}
              vendorId={order.vendorId}
              locationName={locationName}
              locationAddress={locationAddress}
              isPendingFeedback={order.isPendingFeedback}
              isTrackable={false}
              canReorder={false}
              isPast
            />
          </OrderStatus.Layout.Info>

          <OrderStatus.Layout.Content>
            <VStack gap={0} divider={<OrderStatus.Divider />}>
              <Receipt.ClaimBanner
                isLoggedIn={isLoggedIn}
                isClaimed={isClaimed}
                receiptId={receiptId}
              />

              <OrderStatus.Address
                orderType={orderType}
                locationName={locationName}
                locationAddress={locationAddress}
                locationCity={locationCity}
                locationState={locationState}
                locationZipCode={locationZipCode}
              />

              <View>
                <Receipt.LineItems lineItems={order.lineItems} />

                <OrderStatus.Ledger ledger={order.ledger} />
              </View>

              <OrderStatus.Payments payments={payments} />
            </VStack>

            <OrderStatus.ContactUs
              orderId={orderId}
              canCancel={false}
              restaurantId={restaurantId}
              onContactUs={handleContactUs}
              shouldHideCancelOption
            />
          </OrderStatus.Layout.Content>
        </OrderStatus.Layout.SidePanel>
      </OrderStatus.Layout.Container>

      <AppFooter />
    </OrderStatus.Layout.ContainerWrapper>
  );
};

// ─── Unavailable Receipt Screen States ───────────────────────────────────────────────────────────────────

const NotFoundReceiptView = () => {
  const { formatMessage } = useIntl();

  useTrackEffect('receipt.error_screen_viewed', { userError: 'not-found' });

  return (
    <Receipt.UnavailableView
      title={formatMessage(messages.notFoundTitle)}
      description={formatMessage(messages.notFoundDescription)}
    />
  );
};

const ExpiredReceiptView = () => {
  const { formatMessage } = useIntl();

  useTrackEffect('receipt.error_screen_viewed', { userError: 'expired' });

  return (
    <Receipt.UnavailableView
      title={formatMessage(messages.expiredTitle)}
      description={formatMessage(messages.expiredDescription)}
    />
  );
};

const ClaimedReceiptView = () => {
  const { formatMessage } = useIntl();

  useTrackEffect('receipt.error_screen_viewed', { userError: 'already-claimed' });

  return (
    <Receipt.UnavailableView
      title={formatMessage(messages.claimedTitle)}
      description={formatMessage(messages.claimedDescription)}
      shouldDisplayContactUs
    />
  );
};

const UndefinedErrorReceiptView = () => {
  const { formatMessage } = useIntl();

  useTrackEffect('receipt.error_screen_viewed', { userError: 'unknown-error' });

  return <Receipt.UnavailableView title={formatMessage(messages.errorTitle)} />;
};

// ─── Pure Functions ───────────────────────────────────────────────────────────────────

const getOrderType = (orderType?: OrderType, isRestaurantOutpost?: boolean): AddressType => {
  if (isRestaurantOutpost) return 'outpost';
  if (orderType === OrderType.Delivery) return 'delivery';
  if (orderType === OrderType.InStore) return 'in-store';

  return 'pickup';
};

// ─── Messages ───────────────────────────────────────────────────────────────────

const messages = defineMessages({
  claimedTitle: {
    defaultMessage: 'This receipt has already been claimed.',
    description: 'Receipt | Already Claimed View | Title',
  },
  claimedDescription: {
    defaultMessage: "Doesn't look right?",
    description: 'Receipt | Already Claimed View | Description',
  },
  notFoundTitle: {
    defaultMessage: "We couldn't find that receipt",
    description: 'Receipt | Not Found View | Title',
  },
  notFoundDescription: {
    defaultMessage: 'Need help finding your receipt?',
    description: 'Receipt | Expired View | Description',
  },
  expiredTitle: {
    defaultMessage: 'This receipt is older than 30 days and has expired.',
    description: 'Receipt | Expired View | Title',
  },
  expiredDescription: {
    defaultMessage: 'Do you still need this receipt?',
    description: 'Receipt | Expired View | Description',
  },
  errorTitle: {
    defaultMessage: 'We encountered an error while fetching your receipt.',
    description: 'Receipt | Unknown Error View | Title',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ReceiptData = {
  receiptData: Extract<ReceiptQuery['receipt'], { readonly __typename: 'Receipt' }>;
};

type ReceiptScreenReadyProps = ReceiptData & { isLoggedIn: boolean };
